/** Field types excluded from Excel template (display / navigation / non-input) */
import { isValidEmail } from './emailValidation'
import { supportsRecipientNameToggle } from '../../../../components/formBuilder/editForm/formFieldTypes'

export const PREFILL_MAX_IMPORT_ROWS = 1000
export const PREFILL_MAX_FILE_BYTES = 15 * 1024 * 1024

export const PREFILL_EXCLUDED_TYPES = new Set([
  'heading',
  'subheading',
  'banner',
  'formLogo',
  'divider',
  'button',
  'prevPageButton',
  'nextPageButton',
  'captcha',
  'signature',
  'addPage',
  'subform',
  'buttonRow'
])

const getFieldColumnLabel = (element: any): string | null => {
  const candidates = [element?.label, element?.placeholder, element?.name]
  for (const value of candidates) {
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim()
    }
  }
  return null
}

const normalizeFormPages = (formData: any): any[][] => {
  if (!Array.isArray(formData) || formData.length === 0) return []
  if (Array.isArray(formData[0])) return formData
  return [formData]
}

export const detectRecipientNameFieldIdFromFormData = (formData: any): number | undefined => {
  const pages = normalizeFormPages(formData)
  for (const page of pages) {
    if (Array.isArray(page)) {
      for (const element of page) {
        if (supportsRecipientNameToggle(element) && element?.useAsRecipientName === true) {
          const fieldId = element.fieldId ?? element.id
          if (fieldId !== undefined && fieldId !== null) {
            return Number(fieldId)
          }
        }
      }
    }
  }
  return undefined
}

export const flattenMappableFormFields = (
  formData: any
): { fieldId: number; label: string; type: string; useAsRecipientName?: boolean }[] => {
  const pages = normalizeFormPages(formData)
  const fields: { fieldId: number; label: string; type: string; useAsRecipientName?: boolean }[] =
    []

  pages.forEach((page: any[]) => {
    if (!Array.isArray(page)) return
    page.forEach((el: any) => {
      if (!el?.type || PREFILL_EXCLUDED_TYPES.has(el.type)) return
      const label = getFieldColumnLabel(el)
      if (!label) return
      const fieldId = el.fieldId ?? el.id
      if (fieldId === undefined || fieldId === null) return
      fields.push({
        fieldId: Number(fieldId),
        label,
        type: el.type,
        useAsRecipientName: el.useAsRecipientName === true
      })
    })
  })

  const seenLabels = new Set<string>()
  return fields.map((field) => {
    let uniqueLabel = field.label
    if (seenLabels.has(uniqueLabel)) {
      uniqueLabel = `${field.label} (${field.fieldId})`
    }
    seenLabels.add(uniqueLabel)
    return { ...field, label: uniqueLabel }
  })
}

type DeliveryFieldLike = {
  fieldId?: number
  label?: string
  type?: string
  formFieldId?: number
  formFieldLabel?: string
  formFieldType?: string
  useAsRecipientName?: boolean
}

const normalizeDeliveryField = (field: DeliveryFieldLike) => ({
  fieldId: field.fieldId ?? field.formFieldId ?? 0,
  label: field.label ?? field.formFieldLabel ?? '',
  type: field.type ?? field.formFieldType ?? '',
  useAsRecipientName: field.useAsRecipientName === true
})

const isEmailLikeField = (field: { label: string; type: string }): boolean =>
  field.type === 'email' || /email/i.test(field.label)

const isNameLikeField = (field: { label: string }): boolean => /name/i.test(field.label)

const findEmailFieldId = (fields: DeliveryFieldLike[]): number | undefined => {
  const normalized = fields.map(normalizeDeliveryField)
  const byType = normalized.find((f) => f.type === 'email')
  if (byType) return byType.fieldId
  return normalized.find((f) => isEmailLikeField(f))?.fieldId
}

export const detectDeliveryEmailFieldId = (
  fields: DeliveryFieldLike[],
  explicit?: number,
  fallbackFields?: DeliveryFieldLike[]
): number | undefined => {
  if (explicit) return explicit
  const fromFields = findEmailFieldId(fields)
  if (fromFields) return fromFields
  if (fallbackFields?.length) {
    return findEmailFieldId(fallbackFields)
  }
  return undefined
}

export const normalizeColumnHeader = (value: string): string => {
  let normalized = value.trim()
  if (normalized.endsWith('*')) {
    normalized = normalized.slice(0, -1).trimEnd()
  }
  normalized = normalized.replace(/\s+/g, ' ').toLowerCase()
  if (normalized.endsWith(':')) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

export const getRowColumnValue = (row: Record<string, string>, columnName?: string): string => {
  if (!columnName) return ''
  const direct = row[columnName]?.trim()
  if (direct) return direct

  const normalizedTarget = normalizeColumnHeader(columnName)
  for (const [key, value] of Object.entries(row)) {
    if (normalizeColumnHeader(key) === normalizedTarget && value?.trim()) {
      return value.trim()
    }
  }
  return ''
}

export const findMatchingExcelColumn = (
  columnName: string,
  columns: string[]
): string | undefined => {
  if (columns.includes(columnName)) return columnName
  const normalizedTarget = normalizeColumnHeader(columnName)
  return columns.find((column) => normalizeColumnHeader(column) === normalizedTarget)
}

export const findMatchingFormField = <T extends { fieldId: number; label: string; type: string }>(
  columnName: string,
  fields: T[]
): T | undefined => {
  const exact = fields.find((field) => field.label === columnName)
  if (exact) return exact
  const normalizedColumn = normalizeColumnHeader(columnName)
  return fields.find((field) => normalizeColumnHeader(field.label) === normalizedColumn)
}

export type ExcelFieldLike = {
  type: string
  isEditField?: boolean
  prefillEditable?: boolean
  prefillEditMode?: string
}

/** Editable flag for import — mirrors form builder prefill settings. */
export const resolveExcelFieldEditable = (field: ExcelFieldLike): boolean => {
  if (field.isEditField) return true
  if (field.prefillEditable === false) return false
  if (field.prefillEditMode === 'editFields' || field.prefillEditMode === 'conditional') {
    return true
  }
  if (field.prefillEditable !== undefined) return field.prefillEditable
  return field.type !== 'email' && field.type !== 'phone'
}

export const getEmailFormField = (
  fields: { fieldId: number; label: string; type: string }[]
): { fieldId: number; label: string; type: string } | undefined => {
  const byType = fields.find((f) => f.type === 'email')
  if (byType) return byType
  return fields.find((f) => isEmailLikeField(f))
}

export const validateRowEmails = (
  rows: Record<string, string>[],
  emailColumnName: string
): string[] => {
  const errors: string[] = []
  rows.forEach((row, index) => {
    const rowNum = index + 2
    const value = getRowColumnValue(row, emailColumnName)
    if (!value) {
      errors.push(`Row ${rowNum}: missing value in "${emailColumnName}" column`)
      return
    }
    if (!isValidEmail(value)) {
      errors.push(`Row ${rowNum}: invalid email in "${emailColumnName}" column (${value})`)
    }
  })
  return errors
}

export const detectRecipientNameFieldIdFromFields = (
  fields: DeliveryFieldLike[]
): number | undefined =>
  fields.find(
    (field) =>
      supportsRecipientNameToggle({
        type: field.type ?? field.formFieldType,
        label: field.label ?? field.formFieldLabel
      }) && field.useAsRecipientName === true
  )?.fieldId ??
  fields.find(
    (field) =>
      field.formFieldType === 'text' &&
      field.useAsRecipientName === true &&
      supportsRecipientNameToggle({ type: field.formFieldType, label: field.formFieldLabel })
  )?.formFieldId

export const detectDeliveryNameFieldId = (
  fields: DeliveryFieldLike[],
  emailFieldId?: number,
  explicit?: number
): number | undefined => {
  if (explicit) return explicit
  const normalized = fields.map(normalizeDeliveryField)
  const flagged = normalized
    .filter((field) => field.fieldId !== emailFieldId)
    .find(
      (field) =>
        field.useAsRecipientName === true &&
        supportsRecipientNameToggle({ type: field.type, label: field.label })
    )
  if (flagged) return flagged.fieldId
  return normalized.filter((f) => f.fieldId !== emailFieldId).find((f) => isNameLikeField(f))
    ?.fieldId
}
